/**
 * CAD OIDC redirect_uri always targets the Load app's callback route.
 * In local dev, sibling SPAs run on other ports (8087, 8088) but must still
 * register http://localhost:8086/Load/login/callback with the IdP.
 */
export const CAD_LOAD_CALLBACK_PATH = '/Load/login/callback';

/** Default Load dev server port — keep in sync with Load-React vite.config.ts */
export const CAD_LOAD_DEV_ORIGIN = 'http://localhost:8086';

function isSiblingDevPort(): boolean {
  const port = window.location.port;
  return port === '8087' || port === '8088';
}

export function getCadCallbackOrigin(): string {
  // Security (:8087) and WebReview (:8088) never host the shared callback.
  if (typeof window !== 'undefined' && isSiblingDevPort()) {
    return CAD_LOAD_DEV_ORIGIN;
  }
  const fromEnv =
    typeof import.meta !== 'undefined' &&
    (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
      ?.VITE_CAD_CALLBACK_ORIGIN;

  if (fromEnv?.trim()) {
    return fromEnv.replace(/\/$/, '');
  }
  return window.location.origin;
}

export function getCadRedirectUri(): string {
  return `${getCadCallbackOrigin()}${CAD_LOAD_CALLBACK_PATH}`;
}

/** Public origin of the SPA that initiated login (e.g. Security on :8087). */
export function getAppPublicOrigin(): string | undefined {
  const fromEnv =
    typeof import.meta !== 'undefined' &&
    (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
      ?.VITE_APP_PUBLIC_ORIGIN;
  if (fromEnv?.trim()) {
    return fromEnv.replace(/\/$/, '');
  }
  return undefined;
}

/**
 * Always produce an absolute return URL. Path-only values (e.g. "/Security/")
 * must resolve against the initiating app's host/port, not the Load callback
 * host where this code often runs (:8086).
 */
export function resolveCadReturnTo(
  returnTo: string | undefined,
  fallbackBasePath: string,
  initiatingOrigin?: string
): string {
  const appOrigin =
    initiatingOrigin?.replace(/\/$/, '') || getAppPublicOrigin() || window.location.origin;

  const basePath = fallbackBasePath.startsWith('/') ? fallbackBasePath : `/${fallbackBasePath}`;
  const fallbackUrl = `${appOrigin}${basePath}`;

  if (!returnTo?.trim()) {
    return fallbackUrl;
  }

  const trimmed = returnTo.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const base = appOrigin.endsWith('/') ? appOrigin : `${appOrigin}/`;
  const path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  return new URL(path, base).href;
}

export function isCrossPortCadHandoff(returnTo: string): boolean {
  try {
    return new URL(returnTo).origin !== window.location.origin;
  } catch {
    return false;
  }
}

function utf8ToBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach(b => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

function base64ToUtf8(encoded: string): string {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

/**
 * Flat token shape posted to the desktop WebView2 host via the `cad-auth-ready`
 * message. This is the ONLY consumer of the flat fields — the C# host
 * (`LogOnPresenter.CadAuthMessage`) reads these camelCase keys and cannot parse
 * the oidc-client-ts storage string. The SPA cross-port handoff does NOT use
 * this; it carries `user.toStorageString()` verbatim (see below).
 */
export interface CadTokenBundle {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  issuer?: string;
}

/**
 * Append the oidc-client-ts serialized User (`user.toStorageString()`) to
 * returnTo when redirecting to another localhost dev-server port. The sibling
 * restores it verbatim via `User.fromStorageString` + `userManager.storeUser`,
 * so oidc-client-ts owns/persists all user data — nothing is reconstructed by
 * hand and no token fields are parsed off the wire.
 */
export function appendCadHandoffToUrl(returnTo: string, userStorage: string): string {
  if (!isCrossPortCadHandoff(returnTo)) {
    return returnTo;
  }
  const encoded = encodeURIComponent(utf8ToBase64(userStorage));
  const url = new URL(returnTo);
  url.hash = `cad_auth=${encoded}`;
  return url.toString();
}

/**
 * Read and remove the cad_auth hash fragment after a cross-port redirect.
 * Returns the oidc-client-ts serialized User string, or null if absent.
 */
export function consumeCadHandoffFromUrl(): string | null {
  const hash = window.location.hash;
  if (!hash.includes('cad_auth=')) {
    return null;
  }
  const match = hash.match(/cad_auth=([^&]*)/);
  if (!match?.[1]) {
    return null;
  }
  try {
    const userStorage = base64ToUtf8(decodeURIComponent(match[1]));
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, '', clean);
    return userStorage;
  } catch {
    return null;
  }
}
